<script setup>
// TapDatePicker — THE date field (mockup .tdp kit, admin-club-meet-form-when-
// oneoff.html): a typed, masked half plus a compact calendar popover with
// quick chips, month and year selects, ‹ › month nav and a hand-rolled month
// grid — 36px targets, today ringed in brand, selected brand-filled,
// out-of-range days disabled, adjacent-month days muted but tappable.
//
// v-model is an ISO string, NOT the text in the box. The mask, placeholder and
// field order all come from the reader's locale via useDates, and the LABEL
// always states the order it wants ("Start date (dd/mm/yyyy)" · "(mm/dd/yyyy)")
// — a box holding "08/12/2026" with nothing saying the order is exactly how
// the wrong date gets read. So every use passes a `label`.
//
// THE DEFAULTS ARE SCHEDULING DEFAULTS: `min` is today and the chips are
// Today / Tomorrow / In a week, because this started life as the meet-date
// field. Any question about a date in the PAST must say so — `min` and `max`
// bound the window and `quick` replaces or empties the chip row. A birthday
// asked with the defaults has every day disabled and cannot be answered at
// all, which is exactly what shipped before this was fixed.
//
// The month and year SELECTS exist because ‹ › alone is a month per click:
// reaching a 1988 birthday took about 450 of them. They are offered only
// within the window, so the grid can never be scrolled somewhere nothing is
// selectable.
import {computed, ref, watch} from 'vue';
import {vMaska} from 'maska/vue';
import {DateTime} from 'luxon';
import {useDates} from '~/composables/useDates';
import {useDeviceLocale} from '~/composables/useDeviceLocale';
import {
  canStep, chipsToOffer, dateOfBirthPickerStart, dayOutOfRange, monthToOpenOn, monthToShow, monthsToOffer,
  opensOnYears, yearsToOffer,
} from '~/composables/useDatePickerRange';

const props = defineProps({
  label: {type: String, default: null},
  error: {type: Boolean, default: false},
  errorMessages: {type: [Array, String], default: () => []},
  hint: {type: String, default: null},
  /** BCP-47 tag. Defaults to the device locale; pass one to override. */
  locale: {type: String, default: null},
  // An ISO date — days before it are disabled. Defaults to today; pass an
  // empty string to allow any date.
  min: {type: String, default: undefined},
  // An ISO date — days AFTER it are disabled. No maximum by default; pass
  // today's date for a question that cannot be answered with the future.
  max: {type: String, default: undefined},
  // The chip row. Days are counted from today, so these only make sense for a
  // date near today; pass [] to drop the row entirely.
  // A date of birth. With no answer yet the popover opens on a list of years,
  // about 25 years back, and today is not ringed: opening on today made today
  // the easiest pick. Every other use keeps the month grid.
  birthDate: {type: Boolean, default: false},
  quick: {
    type: Array,
    default: () => [
      {label: 'Today', days: 0},
      {label: 'Tomorrow', days: 1},
      {label: 'In a week', days: 7},
    ],
  },
});

// ISO in, ISO out. Never the display text.
const model = defineModel();

const {dateInputDisplay, dateInputCommit, dateInputMask, dateInputPlaceholder, currentLocale} =
  useDates();

const deviceLocale = useDeviceLocale();
const activeLocale = computed(() => props.locale || deviceLocale.value || currentLocale());

const maskOptions = computed(() => ({mask: dateInputMask(activeLocale.value), eager: true}));
const placeholder = computed(() => dateInputPlaceholder(activeLocale.value));

// The label carries the order it wants — a box holding "08/12/2026" with
// nothing stating the order is how the wrong date gets read.
const labelWithFormat = computed(() =>
  props.label ? `${props.label} (${placeholder.value})` : null,
);

const menu = ref(false);

const text = ref(dateInputDisplay(model.value, activeLocale.value));
const invalid = ref(false);

// Re-render on an EXTERNAL change only — never while the text already means the
// model, or we would rewrite what someone is part-way through typing.
watch(
  () => [model.value, activeLocale.value],
  () => {
    const current = dateInputCommit(text.value, activeLocale.value);
    if ((current.iso ?? null) !== (model.value ?? null)) {
      text.value = dateInputDisplay(model.value, activeLocale.value);
      invalid.value = false;
    }
  },
);

watch(text, (typed) => {
  const result = dateInputCommit(typed, activeLocale.value);
  invalid.value = result.invalid;
  // A half-deleted field commits null, never the stale previous date.
  model.value = result.iso;
});

const messages = computed(() => {
  if (invalid.value) return [`Enter a real date as ${placeholder.value}`];
  return props.errorMessages;
});
const hasError = computed(() => props.error || invalid.value);

// ── The calendar half ──────────────────────────────────────────────────────
// Luxon drives the grid (month arithmetic, week starts), but a picked day is
// turned into a stored value by the SAME functions the typed half uses, via
// its display text. It looks like a detour and it is the point: a click and a
// typed date for the same day cannot produce different values, because the
// click goes through the typing path.
const today = () => DateTime.now().startOf('day');
const isoFor = (day) =>
  dateInputCommit(dateInputDisplay(day.toJSDate(), activeLocale.value), activeLocale.value).iso;

const asDay = (isoValue) => {
  if (!isoValue) return null;
  const d = DateTime.fromJSDate(new Date(isoValue));
  return d.isValid ? d.startOf('day') : null;
};

const selected = computed(() => asDay(model.value));
const minDay = computed(() => (props.min === undefined ? today() : asDay(props.min)));
const maxDay = computed(() => (props.max === undefined ? null : asDay(props.max)));

// Everything about WHICH days, months and years may be offered lives in
// useDatePickerRange, so it can be tested without a DOM. This component only
// wires it to the markup.
const openAt = () => (props.birthDate
  ? dateOfBirthPickerStart({selected: selected.value, min: minDay.value, max: maxDay.value}).startOf('month')
  : monthToOpenOn({selected: selected.value, min: minDay.value, max: maxDay.value}));

// The year list a date of birth opens on. Picking a year drops to that year's
// month grid; the answer is still a day the member picks.
const pickingYear = ref(false);
const pickYear = (year) => {
  showMonth(year, viewMonth.value.month);
  pickingYear.value = false;
};

// Month shown in the grid — snaps to the selection when the popover opens or
// a full date is typed.
const viewMonth = ref(openAt());
watch(menu, (open) => {
  if (open) {
    viewMonth.value = openAt();
    pickingYear.value = opensOnYears({birthDate: props.birthDate, selected: selected.value});
  }
});
watch(selected, (d) => {
  if (d) viewMonth.value = d.startOf('month');
});

const dowLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const yearOptions = computed(() => yearsToOffer({
  min: minDay.value,
  max: maxDay.value,
  viewYear: viewMonth.value.year,
  selectedYear: selected.value?.year,
}));
const monthOptions = computed(() => monthsToOffer(viewMonth.value.year, minDay.value, maxDay.value));

const showMonth = (year, month) => {
  const target = monthToShow(Number(year), Number(month), minDay.value, maxDay.value);
  if (target) viewMonth.value = DateTime.fromObject({year: target.year, month: target.month, day: 1});
};

const viewYear = computed({
  get: () => viewMonth.value.year,
  set: (year) => showMonth(year, viewMonth.value.month),
});
const viewMonthNumber = computed({
  get: () => viewMonth.value.month,
  set: (month) => showMonth(viewMonth.value.year, month),
});

const step = (months) => {
  const target = viewMonth.value.plus({months});
  showMonth(target.year, target.month);
};
const canStepBack = computed(() => canStep(viewMonth.value, -1, minDay.value, maxDay.value));
const canStepOn = computed(() => canStep(viewMonth.value, 1, minDay.value, maxDay.value));

const grid = computed(() => {
  const days = [];
  const start = viewMonth.value.startOf('week'); // Monday
  const end = viewMonth.value.endOf('month').endOf('week');
  for (let d = start; d <= end; d = d.plus({days: 1})) {
    days.push({
      key: d.toISODate(),
      date: d,
      label: d.day,
      mut: d.month !== viewMonth.value.month,
      today: !props.birthDate && d.hasSame(today(), 'day'),
      sel: selected.value != null && d.hasSame(selected.value, 'day'),
      // One flag for both edges: the grid greys an unpickable day the same way
      // whichever side of the window it falls outside.
      out: dayOutOfRange(d, minDay.value, maxDay.value),
    });
  }
  return days;
});

const commitDay = (day) => {
  text.value = dateInputDisplay(isoFor(day), activeLocale.value);
  menu.value = false;
};

const pick = (cell) => {
  if (cell.out) return;
  commitDay(cell.date);
};

const quickChips = computed(() => chipsToOffer(props.quick, minDay.value, maxDay.value));
const pickQuick = (chip) => commitDay(today().plus({days: chip.days}));
</script>

<template>
  <div>
    <div v-if="labelWithFormat" class="tl-label mb-2">{{ labelWithFormat }}</div>
    <!-- min-width 0 stops v-menu stretching to the activator's width, and the
         content-class strips the default surface panel so only the .tdp card
         shows — without them the calendar floats inside a huge white box. -->
    <v-menu
        v-model="menu"
        :close-on-content-click="false"
        location="bottom start"
        :offset="6"
        :min-width="0"
        content-class="tdp-menu"
    >
      <template #activator="{props: menuProps}">
        <v-text-field
            v-bind="menuProps"
            v-maska="maskOptions"
            v-model="text"
            :placeholder="placeholder"
            prepend-inner-icon="mdi-calendar-blank-outline"
            append-inner-icon="mdi-unfold-more-horizontal"
            :error="hasError"
            :hide-details="!hasError && !hint"
            :error-messages="messages"
            :hint="hint"
            :persistent-hint="hint != null"
        />
      </template>

      <div class="tdp">
        <div v-if="quickChips.length" class="tdp-quick">
          <button
              v-for="chip in quickChips"
              :key="chip.label"
              type="button"
              @click="pickQuick(chip)"
          >{{ chip.label }}</button>
        </div>
        <div v-if="pickingYear" class="tdp-years" role="listbox" aria-label="Year">
          <div class="tdp-years-title">Choose a year</div>
          <div class="tdp-years-grid">
            <button
                v-for="y in yearOptions"
                :key="y"
                type="button"
                class="tdp-year"
                :class="{'tdp-year--at': y === viewMonth.year}"
                :ref="(el) => { if (el && y === viewMonth.year) el.scrollIntoView?.({block: 'center'}); }"
                @click="pickYear(y)"
            >{{ y }}</button>
          </div>
        </div>
        <template v-else>
        <div class="tdp-head">
          <button
              type="button"
              class="tdp-step"
              aria-label="Previous month"
              :disabled="!canStepBack"
              @click="step(-1)"
          >
            <v-icon size="20">mdi-chevron-left</v-icon>
          </button>
          <!-- Native selects on purpose: they teleport nothing, need no second
               overlay inside this one, and open scrolled to the current value
               on every platform. -->
          <div class="tdp-jump">
            <select v-model="viewMonthNumber" aria-label="Month">
              <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
            <select v-model="viewYear" aria-label="Year">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <button
              type="button"
              class="tdp-step"
              aria-label="Next month"
              :disabled="!canStepOn"
              @click="step(1)"
          >
            <v-icon size="20">mdi-chevron-right</v-icon>
          </button>
        </div>
        <div class="tdp-grid">
          <span v-for="(d, i) in dowLabels" :key="`dow-${i}`" class="tdp-dow">{{ d }}</span>
          <span
              v-for="cell in grid"
              :key="cell.key"
              class="tdp-day"
              :class="{
                'tdp-day--mut': cell.mut,
                'tdp-day--today': cell.today,
                'tdp-day--sel': cell.sel,
                'tdp-day--out': cell.out,
              }"
              @click="pick(cell)"
          >{{ cell.label }}</span>
        </div>
        </template>
      </div>
    </v-menu>
  </div>
</template>

<style lang="scss">
/* Unscoped: the menu content teleports to body, so scoped styles can't reach
   it. Kill Vuetify's default menu surface — the .tdp card is the whole UI. */
.tdp-menu.v-overlay__content {
  min-width: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 16px;
}
</style>

<style scoped lang="scss">
.tdp {
  width: 292px;
  background: rgb(var(--v-theme-surface));
  border: 1.5px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
  padding: 12px;
}

.tdp-years-title {
  font-size: 12px;
  font-weight: 700;
  margin: 2px 4px 8px;
  color: rgb(var(--v-theme-onSurface));
}

.tdp-years-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  max-height: 240px;
  overflow-y: auto;
}

.tdp-year {
  height: 36px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-onSurface));

  &:hover {
    background: rgba(var(--v-theme-primary), 0.08);
  }
}

.tdp-year--at {
  outline: 1.5px solid rgb(var(--v-theme-primary));
}

.tdp-quick {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;

  button {
    flex: 1;
    text-align: center;
    font-size: 11.5px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    background: none;
    border: 1.5px solid rgb(var(--v-theme-borderLight));
    border-radius: 999px;
    padding: 6px 0;
    cursor: pointer;

    &:hover {
      border-color: rgb(var(--v-theme-textSubtle));
    }
  }
}

.tdp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 2px 2px 8px;
}

.tdp-jump {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  select {
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    color: rgb(var(--v-theme-on-surface));
    background: none;
    border: 1.5px solid transparent;
    border-radius: 8px;
    padding: 3px 4px;
    cursor: pointer;
    appearance: menulist;

    &:hover {
      border-color: rgb(var(--v-theme-borderLight));
    }

    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: 1px;
    }

    option {
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
      background: rgb(var(--v-theme-surface));
    }
  }
}

.tdp-step {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: none;
  border: none;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgb(var(--v-theme-light));
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.tdp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.tdp-dow {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
  padding: 4px 0 6px;
  text-transform: uppercase;
}

.tdp-day {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  border-radius: 10px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &--mut {
    color: rgb(var(--v-theme-textSubtle));
    opacity: 0.45;
  }

  &--today {
    box-shadow: inset 0 0 0 1.5px rgb(var(--v-theme-primary));
  }

  &--sel {
    background: rgb(var(--v-theme-primary));
    color: #fff;
    font-weight: 700;

    &:hover {
      background: rgb(var(--v-theme-primary));
    }
  }

  &--out {
    color: rgb(var(--v-theme-textSubtle));
    opacity: 0.35;
    cursor: default;

    &:hover {
      background: none;
    }
  }
}
</style>
